import { useMemo, useState } from 'react';
import CpmButton from '../../../components/design-system/Button';
import CpmTooltip from '../../../components/design-system/Tooltip';
import type { CataloguePermission } from '../../../types/accessManagement.types';
import { STARTER_COPY } from '../starterCopy';

interface PermissionRailProps {
    permissions: CataloguePermission[];
    selectedIds: string[];
    onChange: (ids: string[]) => void;
}

interface Group {
    title: string;
    items: CataloguePermission[];
}

function withImplies(ids: string[], permissions: CataloguePermission[]): string[] {
    const byId = new Map(permissions.map((permission) => [permission.id, permission]));
    const next = new Set<string>();
    const queue = [...ids];
    while (queue.length) {
        const id = queue.shift();
        if (!id || next.has(id) || !byId.has(id)) continue;
        next.add(id);
        const implies = byId.get(id)?.implies ?? [];
        implies.forEach((implied) => {
            if (!next.has(implied)) queue.push(implied);
        });
    }
    return permissions.map((permission) => permission.id).filter((id) => next.has(id));
}

function RailBox({ checked }: { checked: boolean }) {
    return (
        <span
            style={{
                width: 17,
                height: 17,
                flex: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 5,
                border: checked ? '1.5px solid #6E64F0' : '1.5px solid var(--cpm-border-strong)',
                background: checked ? '#6E64F0' : '#fff',
            }}
        >
            {checked ? (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                    <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            ) : null}
        </span>
    );
}

function PutAllInButton({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
    const [hover, setHover] = useState(false);
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                background: 'transparent',
                border: 'none',
                height: 'auto',
                padding: '4px 2px',
                fontFamily: 'inherit',
                fontSize: 12.5,
                fontWeight: 800,
                color: hover && !disabled ? '#0E0858' : '#6E64F0',
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'default' : 'pointer',
            }}
        >
            {STARTER_COPY.PUT_ALL_IN}
        </button>
    );
}

function InfoIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" strokeLinecap="round" />
            <path d="M12 8h.01" strokeLinecap="round" />
        </svg>
    );
}

export default function PermissionRail({ permissions, selectedIds, onChange }: PermissionRailProps) {
    const [query, setQuery] = useState('');
    const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
    const byId = useMemo(() => new Map(permissions.map((permission) => [permission.id, permission])), [permissions]);

    const groups = useMemo(() => {
        const order: Group[] = [];
        const index = new Map<string, Group>();
        permissions.forEach((permission) => {
            const existing = index.get(permission.group);
            if (existing) {
                existing.items.push(permission);
                return;
            }
            const group = { title: permission.group, items: [permission] };
            index.set(permission.group, group);
            order.push(group);
        });
        return order;
    }, [permissions]);

    const q = query.trim().toLowerCase();
    const visibleGroups = groups
        .map((group) => ({
            ...group,
            shown: q ? group.items.filter((item) => item.label.toLowerCase().includes(q)) : group.items,
        }))
        .filter((group) => group.shown.length > 0);

    const turnOn = (ids: string[]) => {
        onChange(withImplies([...selectedIds, ...ids], permissions));
    };

    const turnOff = (id: string) => {
        onChange(selectedIds.filter((value) => value !== id));
    };

    const onCount = permissions.filter((permission) => selected.has(permission.id)).length;

    return (
        <div
            style={{
                background: '#fff',
                border: '1px solid var(--cpm-border-default)',
                borderRadius: 16,
                boxShadow: 'var(--cpm-shadow-card)',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                className="flex items-center justify-between flex-wrap"
                style={{ gap: 14, padding: '16px 20px', borderBottom: '1px solid var(--cpm-border-default)' }}
            >
                <div className="flex items-baseline" style={{ gap: 10 }}>
                    <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em' }}>
                        {STARTER_COPY.PERMISSIONS}
                    </span>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--cpm-text-secondary)' }}>
                        {onCount} of {permissions.length} on
                    </span>
                </div>
                <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={STARTER_COPY.SEARCH_PERMISSIONS}
                    aria-label={STARTER_COPY.SEARCH_PERMISSIONS}
                    style={{
                        width: 300,
                        maxWidth: '100%',
                        height: 40,
                        boxSizing: 'border-box',
                        padding: '0 12px',
                        borderRadius: 10,
                        border: '1.5px solid var(--cpm-border-strong)',
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: 'var(--cpm-text-primary)',
                        outline: 'none',
                    }}
                />
            </div>

            {visibleGroups.map((group) => {
                const onInGroup = group.items.filter((item) => selected.has(item.id)).length;
                const allOn = onInGroup === group.items.length;
                return (
                    <div
                        key={group.title}
                        style={{
                            padding: '16px 20px 14px',
                            borderBottom: '1px solid var(--cpm-border-default)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                        }}
                    >
                        <div className="flex items-center justify-between" style={{ gap: 12 }}>
                            <div className="flex items-baseline" style={{ gap: 9 }}>
                                <h3
                                    style={{
                                        margin: 0,
                                        fontSize: 14,
                                        fontWeight: 800,
                                        letterSpacing: '-0.01em',
                                        color: 'var(--cpm-brand-navy)',
                                    }}
                                >
                                    {group.title}
                                </h3>
                                <span
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 800,
                                        color: 'var(--cpm-text-secondary)',
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {onInGroup} of {group.items.length}
                                </span>
                            </div>
                            <PutAllInButton
                                disabled={allOn}
                                onClick={() => turnOn(group.items.map((item) => item.id))}
                            />
                        </div>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                columnGap: 14,
                                rowGap: 2,
                            }}
                        >
                            {group.shown.map((item) => {
                                const implied = (item.implies || [])
                                    .map((id) => byId.get(id)?.label)
                                    .filter((label): label is string => !!label);
                                const dep = implied.length ? `Also turns on ${implied.join(', ')}.` : '';
                                return (
                                    <div
                                        key={item.id}
                                        className="flex items-center"
                                        onMouseEnter={(event) => {
                                            event.currentTarget.style.background = '#FAFAFF';
                                        }}
                                        onMouseLeave={(event) => {
                                            event.currentTarget.style.background = '';
                                        }}
                                        style={{ gap: 6, padding: '2px 4px 2px 0', borderRadius: 8 }}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (selected.has(item.id)) turnOff(item.id);
                                                else turnOn([item.id]);
                                            }}
                                            style={{
                                                flex: 1,
                                                minWidth: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                                padding: '8px 9px',
                                                border: 'none',
                                                background: 'transparent',
                                                cursor: 'pointer',
                                                fontFamily: 'inherit',
                                                fontSize: 13,
                                                fontWeight: 700,
                                                lineHeight: 1.35,
                                                color: '#1A1A1A',
                                                textAlign: 'left',
                                            }}
                                        >
                                            <RailBox checked={selected.has(item.id)} />
                                            <span style={{ minWidth: 0 }}>{item.label}</span>
                                        </button>
                                        <CpmTooltip
                                            tone="light"
                                            fit
                                            maxWidth={260}
                                            content={
                                                <span>
                                                    <span style={{ display: 'block', fontWeight: 600, lineHeight: 1.45 }}>
                                                        {item.description}
                                                    </span>
                                                    {dep ? (
                                                        <span
                                                            style={{
                                                                display: 'block',
                                                                marginTop: 5,
                                                                fontWeight: 700,
                                                                color: 'var(--cpm-brand-purple)',
                                                            }}
                                                        >
                                                            {dep}
                                                        </span>
                                                    ) : null}
                                                </span>
                                            }
                                        >
                                            <button
                                                type="button"
                                                aria-label={`About ${item.label}`}
                                                style={{
                                                    width: 26,
                                                    height: 26,
                                                    border: 'none',
                                                    borderRadius: 7,
                                                    background: 'transparent',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    color: 'var(--cpm-text-secondary)',
                                                }}
                                            >
                                                <InfoIcon />
                                            </button>
                                        </CpmTooltip>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            {q && visibleGroups.length === 0 ? (
                <div className="flex flex-col items-center" style={{ gap: 10, padding: '40px 20px', textAlign: 'center' }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--cpm-brand-navy)' }}>
                        {STARTER_COPY.NO_PERMISSION_PREFIX} “{query.trim()}”
                    </div>
                    <CpmButton variant="outline" size="sm" onClick={() => setQuery('')}>
                        {STARTER_COPY.CLEAR_SEARCH}
                    </CpmButton>
                </div>
            ) : null}
        </div>
    );
}
